import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterOutlet, RouterLink, RouterLinkActive, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil, map } from 'rxjs/operators';

// CDK imports
import { BreakpointObserver } from '@angular/cdk/layout';

// Material imports
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';

// Services
import { AuthService } from './core/services/auth.service';
import { User, UserRole } from './shared/models/tournament.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

interface MenuItem {
  label: string;
  icon: string;
  route?: string;
  roles: UserRole[];
  children?: MenuItem[];
  expanded?: boolean;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    // Material modules
    MatIconModule,
    MatSidenavModule,
    MatMenuModule,
    MatDividerModule,
    TranslateModule
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'TatamIQ - Tournament Management System';
  currentPageTitle = 'Dashboard'; // Add this for dynamic page title
  currentUser: User | null = null;
  currentLang = 'uk';
  isMenuOpen = true;
  isMobile = false;
  showSidenav = true;
  isAuthPage = false;

  private destroy$ = new Subject<void>();

  menuItems: MenuItem[] = [];

  private buildMenuItems(): MenuItem[] {
    return [
      {
        label: this.translate.instant('nav.dashboard'),
        icon: 'dashboard',
        route: '/dashboard',
        roles: [UserRole.ADMIN, UserRole.ORGANIZER, UserRole.TEAM_MANAGER]
      },
      {
        label: this.translate.instant('nav.team_applications'),
        icon: 'group_work',
        route: '/team-applications',
        roles: [UserRole.TEAM_MANAGER, UserRole.ORGANIZER, UserRole.ADMIN]
      },
      {
        label: this.translate.instant('nav.tournaments'),
        icon: 'emoji_events',
        route: '/tournaments',
        roles: [UserRole.ADMIN, UserRole.ORGANIZER, UserRole.TEAM_MANAGER]
      },
      {
        label: this.translate.instant('nav.organizations'),
        icon: 'business',
        route: '/organizations',
        roles: [UserRole.ADMIN, UserRole.ORGANIZER, UserRole.TEAM_MANAGER]
      },
      {
        label: this.translate.instant('nav.participants'),
        icon: 'people',
        route: '/participants',
        roles: [UserRole.ADMIN, UserRole.TEAM_MANAGER]
      },
      {
        label: this.translate.instant('nav.administration'),
        icon: 'admin_panel_settings',
        roles: [UserRole.ADMIN],
        expanded: false,
        children: [
          {
            label: this.translate.instant('nav.user_management'),
            icon: 'people_outline',
            route: '/admin/users',
            roles: [UserRole.ADMIN]
          },
          {
            label: this.translate.instant('nav.system_settings'),
            icon: 'settings',
            route: '/admin/settings',
            roles: [UserRole.ADMIN]
          },
          {
            label: this.translate.instant('nav.audit_logs'),
            icon: 'history',
            route: '/admin/logs',
            roles: [UserRole.ADMIN]
          },
          {
            label: this.translate.instant('nav.invitations'),
            icon: 'mail_outline',
            route: '/admin/invitations',
            roles: [UserRole.ADMIN]
          },
          {
            label: this.translate.instant('nav.presets'),
            icon: 'timer',
            route: '/admin/presets',
            roles: [UserRole.ADMIN]
          },
          {
            label: this.translate.instant('nav.data_generation'),
            icon: 'settings_applications',
            route: '/admin/data-generation',
            roles: [UserRole.ADMIN]
          }
        ]
      }
    ];
  }

  constructor(
    public authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private breakpointObserver: BreakpointObserver,
    private translate: TranslateService
  ) {
    translate.setDefaultLang('uk');
    const savedLang = localStorage.getItem('admin-lang');
    this.currentLang = savedLang && (savedLang === 'uk' || savedLang === 'en') ? savedLang : 'uk';
    translate.use(this.currentLang);
  }

  switchLang(lang: string): void {
    this.currentLang = lang;
    this.translate.use(lang);
    localStorage.setItem('admin-lang', lang);
  }

  ngOnInit(): void {
    // Build menu items once translations are ready
    this.menuItems = this.buildMenuItems();
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.menuItems = this.buildMenuItems();
        this.updatePageTitle(this.router.url);
      });

    // Use BreakpointObserver instead of window.innerWidth
    this.breakpointObserver
      .observe(['(max-width: 768px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isMobile = result.matches;
        if (this.isMobile) {
          this.isMenuOpen = false;
        }
      });

    this.authService.currentUser
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
      });

    // Hide sidenav on login page AND update page title
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        // Check if this is an auth page (login, forgot-password, reset-password, accept-invitation, registration)
        this.isAuthPage = event.url.includes('/login') ||
                          event.url.includes('/forgot-password') ||
                          event.url.includes('/reset-password') ||
                          event.url.includes('/accept-invitation') ||
                          event.url.includes('/registration/') ||
                          event.url.includes('/desk/');

        this.showSidenav = !this.isAuthPage;
        if (this.isMobile && this.showSidenav) {
          this.isMenuOpen = false;
        }

        // Update page title based on current route
        this.updatePageTitle(event.url);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  /**
   * Returns the sidenav width for CSS variable binding.
   * Used by fixed-position elements to align with main content.
   */
  get sidenavWidth(): string {
    // Only account for sidenav when it's open in 'side' mode (not mobile 'over' mode)
    const sidenavPushesContent = this.isMenuOpen && !this.isMobile && this.showSidenav && this.currentUser;
    return sidenavPushesContent ? '280px' : '0px';
  }

  toggleMenuExpansion(item: MenuItem): void {
    if (item.children) {
      item.expanded = !item.expanded;
    }
  }

  isMenuItemVisible(item: MenuItem): boolean {
    if (!this.currentUser) return false;
    return item.roles.includes(this.currentUser.role);
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
    if (this.isMobile) {
      this.isMenuOpen = false;
    }
  }

  logout(): void {
    this.authService.logout();
  }

  getUserDisplayName(): string {
    const u = this.currentUser;
    if (!u) return 'User';
    if (u.firstName || u.lastName) {
      return `${u.firstName || ''} ${u.lastName || ''}`.trim();
    }
    return u.email || 'User';
  }

  getUserRole(): string {
    return this.currentUser?.role || '';
  }

  // Add this method for dynamic page title
  getPageTitle(): string {
    return this.currentPageTitle;
  }

  private updatePageTitle(url: string): void {
    const routeTitleMap: { [key: string]: string } = {
      '/dashboard': this.translate.instant('page_title.dashboard'),
      '/team-applications': this.translate.instant('page_title.team_applications'),
      '/tournaments': this.translate.instant('page_title.tournaments'),
      '/tournaments/create': this.translate.instant('page_title.create_tournament'),
      '/participants': this.translate.instant('page_title.participants'),
      '/organizations': this.translate.instant('page_title.organizations'),
      '/admin/users': this.translate.instant('page_title.user_management'),
      '/admin/settings': this.translate.instant('page_title.system_settings'),
      '/admin/invitations': this.translate.instant('nav.invitations'),
      '/admin/presets': this.translate.instant('nav.presets'),
      '/admin/data-generation': this.translate.instant('nav.data_generation'),
      '/admin/logs': this.translate.instant('page_title.audit_logs'),
      '/profile': this.translate.instant('page_title.my_profile'),
      '/settings': this.translate.instant('page_title.settings')
    };

    if (routeTitleMap[url]) {
      this.currentPageTitle = routeTitleMap[url];
      return;
    }

    for (const route in routeTitleMap) {
      if (url.startsWith(route) && route !== '/') {
        this.currentPageTitle = routeTitleMap[route];
        return;
      }
    }

    this.currentPageTitle = this.translate.instant('page_title.dashboard');
  }
}
